/**
 * Links to the external quiz at get.aheadhealth.com (the
 * personalized-test-plan app). That app is i18next-driven and reads a
 * `?locale=` query param — it supports `en` / `de` / `fr` only, so any
 * other UI locale (e.g. `nl`) maps to `en`.
 *
 * Single source of truth for the URL shape: previously three components
 * each carried their own `buildQuizUrl()`, and they disagreed on the
 * param name (`?lang=` vs `?locale=`). The quiz app reads `?locale=`.
 */

import { applyPersonHash } from "@/lib/person-hash-link"

export const QUIZ_BASE_URL = "https://get.aheadhealth.com/"

/** Locales the external quiz has translations for. Everything else → en. */
export function quizLocaleParam(locale: string): "en" | "de" | "fr" {
  return locale === "de" || locale === "fr" ? locale : "en"
}

/**
 * Attribution params forwarded from the current page across the
 * cross-domain hop so the quiz lands with paid attribution intact.
 */
const FORWARDED_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
] as const

interface ExternalQuizUrlOptions {
  /**
   * When true, forward the {@link FORWARDED_PARAMS} from the current page's
   * query string onto the quiz URL. Client-only (reads `window.location`);
   * a no-op during SSR.
   */
  forwardUtm?: boolean
  /**
   * Explicit query params to set on the URL (e.g. a fixed campaign on the
   * homepage hero CTA). Applied before {@link forwardUtm} so live page
   * attribution wins when both are present.
   */
  utm?: Record<string, string>
}

export function externalQuizUrl(
  locale: string,
  opts: ExternalQuizUrlOptions = {}
): string {
  const url = new URL(QUIZ_BASE_URL)
  url.searchParams.set("locale", quizLocaleParam(locale))

  if (opts.utm) {
    for (const [key, value] of Object.entries(opts.utm)) {
      if (value) url.searchParams.set(key, value)
    }
  }

  if (opts.forwardUtm && typeof window !== "undefined") {
    const incoming = new URLSearchParams(window.location.search)
    for (const key of FORWARDED_PARAMS) {
      const value = incoming.get(key)
      if (value) url.searchParams.set(key, value)
    }
  }

  // Forward the email→app person hash whenever the landing URL carries one,
  // independent of the UTM-forwarding opt-in, so quiz sessions that arrived
  // from an email link stay the same PostHog person.
  applyPersonHash(url.searchParams)

  return url.toString()
}
